import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { useTranslations } from '../../../contexts/translation.context';
import { Typography } from '../../../components';
import { showPartnerSuccess, showError } from '../../../utils/toast';
import { FilterSelect, ModelSearchSelect } from '../../../components/ui';
import {
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  GlobalIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
  SearchInputIc,
  ResetFiltersIc,
  DangerIc,
  AccountIcon,
} from '../../../components/icons';
import { staffApi, GetCPLOrdersParams, CPLOrder, UniqueModelByOfId } from '../../../api/staff';
import { accountsApi } from '../../../api/services/accountsApi';
import CPLOrdersTable from './components/CPLOrdersTable';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';

const CPLOrdersPage: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.cplOrders'), isActive: true }
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // State
  const [orders, setOrders] = useState<CPLOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [localSearch, setLocalSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<{
    status?: 'waiting' | 'in_progress' | 'completed' | 'canceled' | 'deactivated' | 'all';
    source?: 'only_fans' | 'tiktok' | 'google' | 'instagram' | 'reddit' | 'telegram' | 'twitter' | 'youtube' | 'another' | 'all';
    model?: string;
    ownerType?: 'creator' | 'agency' | 'all';
  }>({
    status: undefined,
    source: undefined,
    model: undefined,
    ownerType: undefined,
  });

  // Model search state
  const [availableModels, setAvailableModels] = useState<UniqueModelByOfId[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelSearchTerm, setModelSearchTerm] = useState('');

  // Status options - используем правильные значения из API
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('admin.cplOrders.filters.status.all') },
    { key: 'waiting', label: t('admin.cplOrders.filters.status.waiting') },
    { key: 'in_progress', label: t('admin.cplOrders.filters.status.inProgress') },
    { key: 'completed', label: t('admin.cplOrders.filters.status.completed') },
    { key: 'canceled', label: t('admin.cplOrders.filters.status.canceled') },
  ], [t]);

  // Source options - используем те же значения что и в Offers с иконками
  const sourceOptions = useMemo(() => [
    { key: 'all', label: t('admin.cplOrders.filters.source.all'), icon: React.createElement(GlobalIc, { size: 16 }) },
    { key: 'only_fans', label: 'OnlyFans', icon: React.createElement(OnlyFansIc, { size: 16, fill:'#A8A8B0' }) },
    { key: 'reddit', label: 'Reddit', icon: React.createElement(IcReddit, { size: 16 }) },
    { key: 'instagram', label: 'Instagram', icon: React.createElement(InstagramIc, { size: 16, fill:'#A8A8B0' }) },
    { key: 'tiktok', label: 'TikTok', icon: React.createElement(IcTikTok, { size: 16 }) },
    { key: 'google', label: 'Google', icon: React.createElement(GoogleIc, { size: 16, fill:'#A8A8B0' }) },
    { key: 'telegram', label: 'Telegram', icon: React.createElement(IcTg, { size: 16 }) },
    { key: 'twitter', label: 'Twitter', icon: React.createElement(TwitterIc, { size: 16, fill:'#A8A8B0' }) },
    { key: 'youtube', label: 'YouTube', icon: React.createElement(IcYoutube, { size: 16 }) },
    { key: 'another', label: t('admin.cplOrders.filters.source.other'), icon: React.createElement(IcAnotherSoc, { size: 16 }) },
  ], [t]);

  // Owner type options
  const ownerTypeOptions = useMemo(() => [
    { key: 'all', label: t('admin.cplOrders.filters.ownerType.all') || 'Все' },
    { key: 'creator', label: t('admin.cplOrders.filters.ownerType.creator') || 'Криэйтор' },
    { key: 'agency', label: t('admin.cplOrders.filters.ownerType.agency') || 'Агентство' },
  ], [t]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchValue !== '' ||
           (filters.status !== undefined && filters.status !== 'all') ||
           (filters.source !== undefined && filters.source !== 'all') ||
           (filters.model !== undefined && filters.model !== '') ||
           (filters.ownerType !== undefined && filters.ownerType !== 'all');
  }, [searchValue, filters]);

  // Fetch models for search
  const fetchModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await accountsApi.getUniqueModelsByOfId({
        ...(search && { search }),
      });
      setAvailableModels(response.objects || []);
    } catch (error) {
      console.error('Failed to load models:', error);
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Load initial models
  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  // Handle model search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (modelSearchTerm) {
        fetchModels(modelSearchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [modelSearchTerm, fetchModels]);

  // Fetch CPL orders
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: GetCPLOrdersParams = {
        page: currentPage,
        page_size: pageSize,
        ...(searchValue && { search: searchValue }),
        ...(filters.status && filters.status !== 'all' && {
          status: filters.status as 'waiting' | 'in_progress' | 'completed' | 'canceled' | 'deactivated'
        }),
        ...(filters.source && filters.source !== 'all' && {
          source: filters.source as 'only_fans' | 'tiktok' | 'google' | 'instagram' | 'reddit' | 'telegram' | 'twitter' | 'youtube' | 'another'
        }),
        ...(filters.model && { model: filters.model }),
        ...(filters.ownerType && filters.ownerType !== 'all' && {
          type: filters.ownerType as 'creator' | 'agency'
        }),
      };

      const response = await staffApi.getCPLOrders(params);
      // Map _id to id for table compatibility
      const mappedOrders = (response.objects || []).map(order => ({
        ...order,
        id: order._id
      }));
      setOrders(mappedOrders);
      setTotalCount(response.count);
    } catch (error: any) {
      console.error('Failed to load CPL orders:', error);

      // Показываем ошибку пользователю
      const errorMessage = error?.response?.data?.detail ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Не удалось загрузить заказы';

      showError(errorMessage);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchValue, filters.status, filters.source, filters.model, filters.ownerType]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(localSearch);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Handlers
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value[0] : value;

    if (key === 'status') {
      setFilters(prev => ({
        ...prev,
        status: stringValue === 'all' ? undefined : stringValue as 'waiting' | 'in_progress' | 'completed' | 'canceled' | 'deactivated'
      }));
    } else if (key === 'source') {
      setFilters(prev => ({
        ...prev,
        source: stringValue === 'all' ? undefined : stringValue as 'only_fans' | 'tiktok' | 'google' | 'instagram' | 'reddit' | 'telegram' | 'twitter' | 'youtube' | 'another'
      }));
    } else if (key === 'model') {
      setFilters(prev => ({
        ...prev,
        model: stringValue === 'all' ? undefined : stringValue
      }));
    } else if (key === 'ownerType') {
      setFilters(prev => ({
        ...prev,
        ownerType: stringValue === 'all' ? undefined : stringValue as 'creator' | 'agency'
      }));
    }
    setCurrentPage(1);
  };

  const handleModelSearchChange = (searchTerm: string) => {
    setModelSearchTerm(searchTerm);
  };

  const handleResetFilters = () => {
    setLocalSearch('');
    setSearchValue('');
    setModelSearchTerm('');
    setFilters({
      status: undefined,
      source: undefined,
      model: undefined,
      ownerType: undefined,
    });
    setCurrentPage(1);
    // Reload initial models
    fetchModels();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleDeactivate = useCallback(async (orderId: string) => {
    try {
      await staffApi.deactivateCPLOrder(orderId);

      // Обновляем список заказов
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === orderId ? { ...order, status: 'deactivated' as const } : order
        )
      );

      showPartnerSuccess('Заказ успешно деактивирован');
    } catch (err: any) {
      console.error('Error deactivating order:', err);

      // Извлекаем детальное сообщение об ошибке из ответа API
      const errorMessage = err?.response?.data?.detail ||
                          err?.response?.data?.message ||
                          err?.message ||
                          'Не удалось деактивировать заказ';

      showError(errorMessage);
    }
  }, []);

  return (
    <div className="h-screen flex flex-col">
      {/* Main Content */}
      <div className="flex-1">
        <div className="px-[24px] py-[24px]">
          {/* Title and Filters */}
          <div className="mb-[16px]">
            <Typography variant="h5" className="text-[#11181C] font-semibold mb-[16px]">
              {t('admin.cplOrders.title')}
            </Typography>

            {/* Filters Row */}
            <div className="flex items-center gap-[16px] flex-wrap">
              {/* Search Input */}
              <Input
                type="text"
                placeholder={t('admin.cplOrders.filters.search.placeholder') || 'Имя/ID'}
                size="sm"
                value={localSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                endContent={<SearchInputIc />}
                classNames={{
                  mainWrapper: "w-[273px]",
                  base: "w-[273px]",
                  input: "text-sm",
                  inputWrapper: "rounded-lg h-[36px] w-[273px]",
                }}
              />

              {/* Owner Type Filter */}
              <FilterSelect
                selectedKey={filters.ownerType || 'all'}
                onSelectionChange={(value) => handleFilterChange('ownerType', value)}
                options={ownerTypeOptions}
                placeholder={t('admin.cplOrders.filters.ownerType.placeholder') || 'Владельцы'}
                variant="campaign"
                autoWidth={true}
                showPlaceholderWithValue={true}
              />

              {/* Model Filter */}
              <ModelSearchSelect
                placeholder={t('admin.cplOrders.filters.model.placeholder')}
                options={availableModels.map((model) => ({
                  key: String(model.id || ''),
                  avatar: model.avatar || '',
                  username: model.of_user_name || '',
                  name: model.name || '',
                  label: model.name || ''
                }))}
                selectedKey={filters.model || 'all'}
                onSelectionChange={(value) => handleFilterChange('model', value)}
                onSearchChange={handleModelSearchChange}
                isLoading={modelsLoading}
                startContent={<AccountIcon className="text-[#A8A8B0] stroke-[#A8A8B0]" size={20} />}
                variant="campaign"
                autoWidth={true}
              />

              {/* Source Filter */}
              <FilterSelect
                selectedKey={filters.source || 'all'}
                onSelectionChange={(value) => handleFilterChange('source', value)}
                options={sourceOptions}
                placeholder={t('admin.cplOrders.filters.source.placeholder')}
                variant="campaign"
                autoWidth={true}
              />

              {/* Status Filter */}
              <FilterSelect
                selectedKey={filters.status || 'all'}
                onSelectionChange={(value) => handleFilterChange('status', value)}
                options={statusOptions}
                placeholder={t('admin.cplOrders.filters.status.placeholder')}
                variant="campaign"
                autoWidth={true}
                startContent={<DangerIc size={20}/>}
              />

              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <Tooltip content={t('admin.cplOrders.filters.reset')} placement="top">
                  <Button
                    size="sm"
                    className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                    onPress={handleResetFilters}
                  >
                    <ResetFiltersIc/>
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Table */}
          <CPLOrdersTable
            orders={orders}
            isLoading={isLoading}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onDeactivate={handleDeactivate}
          />
        </div>
      </div>
    </div>
  );
};

export default CPLOrdersPage;
